import getRoutes, { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";

/**
 * Legacy pre-redesign route paths. Any incoming request to one of these paths
 * is silently swallowed here (return null) because a redirect middleware / the
 * router will send the user to the correct new path.
 */
const OLD_URLS: string[] = [
  "/segments/equity",
  "/segments/advertising",
  "/segments/conversion",
  "/segments/emotional",
  "/segments/trends",
  "/segments/profile",
  "/segments/reputation",
  "/segments/visibility",
  "/competitive/equity",
  "/competitive/advertising",
  "/competitive/conversion",
  "/competitive/emotional",
  "/competitive/trends",
  "/competitive/profile",
  "/competitive/reputation",
  "/competitive/visibility",
  "/global/equity",
  "/global/advertising",
  "/global/conversion",
  "/global/emotional",
  "/global/trends",
  "/global/profile",
  "/global/reputation",
  "/global/visibility",
  "/advanced/butterfly",
  "/advanced/prepost",
  "/advanced/perceptual",
  "/advanced/differentiation",
  "/advanced/reputation",
  "/advanced/visibility",
  "/questions/segment",
  "/questions/brand",
];
import type { RootState } from "@/store/store";
import { getFeatureRedirect } from "@/utils/featureStatus";
import { getDisabledFeaturesList } from "@/utils/featuresStatus";
import { setPendingSelection } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { dequal } from "dequal";
import { useCallback, useEffect, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const REDIRECT_SECONDS = 30;

const NoMatch = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [timeLeft, setTimeLeft] = useState(REDIRECT_SECONDS);

  const featureStatus = useSelector(
    (state: RootState) => state.featureStatus,
    (prev, next) => dequal(prev, next)
  );

  const pathname = location.pathname.toLowerCase();
  const moduleId = pathname.split("/")[1] ?? "";
  const isDisabledModule =
    getDisabledFeaturesList(featureStatus).includes(moduleId) &&
    getRoutes().some((route) => route.id === moduleId);
  const isHidden =
    !isDisabledModule &&
    (OLD_URLS.includes(pathname) ||
      Object.keys(ROUTES_CHILDREN_LOOKUP).includes(pathname));

  // featureStatus also holds non-route flags (aiAssistant, legacyScore, ...)
  // that selectionListener can't navigate to.
  const routableFeatures = new Set(getRoutes().map((route) => route.id));
  const homeUrl = getFeatureRedirect(
    Object.fromEntries(
      Object.entries(featureStatus).filter(([feature]) =>
        routableFeatures.has(feature)
      )
    ) as Record<string, Record<string, boolean>>
  );

  const goToHome = useCallback(() => {
    const [module = "", screenType = "brand"] = homeUrl
      .split("/")
      .filter(Boolean);
    // Don't navigate here: selectionListener validates the pending selection
    // and navigates with the full query string, same as a SideNav click.
    dispatch(
      setPendingSelection({
        module,
        screenType,
        ...(module === "reputation" ? { base: "FAMILIAR" } : {}),
      })
    );
  }, [dispatch, homeUrl]);

  useEffect(() => {
    if (isHidden || !homeUrl) return;
    if (timeLeft === 0) {
      goToHome();
      return;
    }
    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, isHidden, homeUrl, goToHome]);

  if (isHidden) {
    return null;
  }

  return (
    <div className="fixed z-[10000000] top-0 left-0 w-[100vw] h-[100vh] bg-[#f5f7fa] flex justify-center items-center">
      <div className="h-lvh flex-col flex justify-center items-center">
        <div className="w-[500px] bg-white p-[30px] border border-[#e4e7ec] bg-white">
          <div className="flex justify-center items-center ">
            <img
              className="w-[200px]"
              src="/images/logo/harris_questbrand_complete_logo.png"
              alt="Harris poll"
            />
          </div>
          <div className="my-[23px] border-y text-[#4A4A4A] border-[#e4e7ec] text-center tracking-[0.2px] [word-spacing:1.5px]">
            <div className="mt-[23px] flex justify-center">
              <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full border-2 border-[#d0d5dd] text-[#667085]">
                <FiAlertCircle className="h-9 w-9" strokeWidth={1.5} />
              </div>
            </div>
            <div className="mt-[16px] mb-[18px] font-[16px] font-bold text-[23px]">
              404 - Page Not Found
            </div>
            <div>
              The Page you are looking for does not exist
            </div>
            <div className="mb-[23px] mt-[16px]">
              Contact{" "}
              <a
                className="text-[#0000ee] underline"
                href="mailto:hbp@harrispoll.com"
              >
                hbp@harrispoll.com
              </a>{" "}
              for any further support.
            </div>
          </div>
          {homeUrl && (
            <div className="flex flex-col items-center gap-[14px] tracking-[0.2px] [word-spacing:1.5px]">
              <button
                type="button"
                onClick={goToHome}
                className="rounded-md cursor-pointer border border-primary-500 bg-transparent px-4 py-2 text-sm font-semibold text-primary-500 transition-colors hover:bg-[#f5f7fa]"
              >
                Go to home
              </button>
              <div className="text-base text-[#4A4A4A] text-center">
                Redirecting in {timeLeft} seconds
              </div>
            </div>
          )}
        </div>
        <div className="text-xs text-[#4A4A4A] text-center mt-[14px]">
          <p>
            &#169; Harris Poll QuestBrand is Copyright of The Harris Poll 2024 -
            2025.
            <br />
            All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NoMatch;
